import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldCheck, ShieldX, Award, Trophy, TrendingUp, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AssessmentPlayer() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [a, setA] = useState(null);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { api.get(`/assessments/${id}`).then((r) => setA(r.data)).catch(() => setA(false)); }, [id]);

    const submit = async () => {
        if (!a) return;
        if (Object.keys(answers).length < a.questions.length) { toast.error("Please answer all questions."); return; }
        setBusy(true);
        try {
            const ordered = a.questions.map((q) => answers[q.id]);
            const { data } = await api.post(`/assessments/${id}/submit`, { answers: ordered });
            setResult(data);
            if (data.passed) toast.success("Passed! Credential and badge awarded.");
        } catch { toast.error("Could not submit assessment."); }
        finally { setBusy(false); }
    };

    if (a === false) return <div className="min-h-screen grid place-items-center bg-background"><p>Assessment not found.</p></div>;
    if (!a) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

    return (
        <div className="min-h-screen bg-muted/30">
            <header className="sticky top-0 z-20 flex items-center gap-3 h-16 px-4 sm:px-8 bg-background/85 backdrop-blur-xl border-b border-border">
                <Link to="/dashboard" className="inline-flex items-center text-sm text-foreground/60 hover:text-primary"><ArrowLeft className="mr-1 h-4 w-4" /> Dashboard</Link>
            </header>
            <div className="max-w-2xl mx-auto p-4 sm:p-8">
                {!result ? (
                    <>
                        <Badge variant="secondary" className="rounded-full">{a.type}</Badge>
                        <h1 className="mt-3 font-heading text-2xl font-bold">{a.title}</h1>
                        <div className="mt-2 flex items-center gap-2 text-sm text-foreground/60"><ShieldAlert className="h-4 w-4 text-accent" /> AI-use mode: <span className="font-medium capitalize">{a.ai_mode}</span> — no AI assistance during this assessment.</div>
                        <div className="mt-6 space-y-5">
                            {a.questions.map((q, i) => (
                                <Card key={q.id} className="p-5" data-testid={`question-${i}`}>
                                    <p className="font-medium"><span className="text-primary font-heading">Q{i + 1}.</span> {q.q}</p>
                                    <RadioGroup className="mt-3 space-y-2" value={answers[q.id]?.toString()} onValueChange={(v) => setAnswers((s) => ({ ...s, [q.id]: Number(v) }))}>
                                        {q.options.map((opt, oi) => (
                                            <div key={oi} className="flex items-center gap-2">
                                                <RadioGroupItem value={oi.toString()} id={`${q.id}-${oi}`} data-testid={`q${i}-opt${oi}`} />
                                                <Label htmlFor={`${q.id}-${oi}`} className="font-normal cursor-pointer">{opt}</Label>
                                            </div>
                                        ))}
                                    </RadioGroup>
                                </Card>
                            ))}
                        </div>
                        <Button onClick={submit} disabled={busy} className="mt-6 w-full rounded-full" data-testid="submit-assessment-btn">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Assessment"}
                        </Button>
                    </>
                ) : (
                    <Card className={`p-8 text-center border-2 ${result.passed ? "border-success/40" : "border-destructive/40"}`} data-testid="assessment-result">
                        <span className={`grid h-16 w-16 mx-auto place-items-center rounded-full ${result.passed ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"}`}>
                            {result.passed ? <ShieldCheck className="h-8 w-8" /> : <ShieldX className="h-8 w-8" />}
                        </span>
                        <h2 className="mt-4 font-heading text-2xl font-bold">{result.passed ? "Passed!" : "Not passed yet"}</h2>
                        <p className="mt-1 text-foreground/60">You scored <span className="font-bold text-foreground">{result.score}%</span> (pass mark {result.pass_score}%)</p>
                        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 text-left">
                            {Object.entries(result.dimensions).map(([d, v]) => (
                                <div key={d} className="rounded-xl bg-muted/50 p-3"><p className="text-xs text-foreground/50">{d}</p><p className="font-heading font-bold">{v} / 4</p></div>
                            ))}
                        </div>
                        {result.passed && (
                            <div className="mt-6 space-y-2 text-sm">
                                {result.awarded.badge && <p className="flex items-center justify-center gap-2"><Trophy className="h-4 w-4 text-accent" /> Badge earned: <b>{result.awarded.badge}</b></p>}
                                {result.awarded.credential && <p className="flex items-center justify-center gap-2"><Award className="h-4 w-4 text-success" /> Credential: <span className="font-mono text-xs">{result.awarded.credential}</span></p>}
                                {result.awarded.lprs != null && <p className="flex items-center justify-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> LPRS updated to <b>{result.awarded.lprs}</b></p>}
                            </div>
                        )}
                        <div className="mt-8 flex gap-2 justify-center">
                            {!result.passed && <Button variant="outline" className="rounded-full" onClick={() => { setResult(null); setAnswers({}); }} data-testid="retry-assessment-btn">Try again</Button>}
                            <Button className="rounded-full" onClick={() => navigate("/dashboard")} data-testid="assessment-done-btn">Back to Dashboard</Button>
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
}
